import type { StrikerType } from "../types";

// Bullseye glass codes that need a "gold hold" strike (~663°C / 2 hours) to
// develop their final colour. Codes are 6-digit roots; material glass_references
// may carry a "-NNNN" suffix that callers strip via `code.split("-")[0]`.
//
// Source: STRIKERS.txt at the project root, distilled from Bullseye technical
// documentation.

export const BULLSEYE_GOLD_STRIKERS: readonly string[] = [
  "001215", // Light Pink
  "001234", // Violet
  "001305", // Sunset Coral
  "001311", // Cranberry Pink
  "001332", // Fuchsia
  "001821", // Erbium Pink Tint
  "001823", // Burnt Scarlet Tint
  "001824", // Ruby Red Tint
  "001831", // Ruby Pink Tint
  "001834", // Coral Orange Tint
  "001932", // Fuchsia Tint
];

export const BULLSEYE_CADMIUM_SELENIUM_STRIKERS: readonly string[] = [
  "000124", // Red Opal
  "000224", // Deep Red Opal
  "000225", // Pimento Red Opal
  "000309", // Cinnabar Opal
  "000321", // Pumpkin Orange
  "000329", // Burnt Orange
  "001025", // Light Orange
  "001120", // Yellow Transparent
  "001122", // Red Transparent
  "001125", // Orange Transparent
];

export const BULLSEYE_TINT_SHIFT_STRIKERS: readonly string[] = [
  "001806", // Juniper Blue Tint
  "001807", // Grass Green Tint
  "001808", // Aqua Blue Tint
  "001814", // Sapphire Blue Tint
  "001816", // Turquoise Blue Tint
  "001818", // Indigo Tint
  "001819", // Brown Topaz Tint
  "001826", // Green Tea Tint
  "001827", // Light Amber Tint
  "001829", // Gray Tint
  "001837", // Medium Amber Tint
  "001841", // Spruce Green Tint
  "001842", // Light Neo-Lavender Shift Tint
  "001844", // Lavender Green Shift Tint
  "001858", // Light Rhubarb Shift Tint
  "001859", // Rhubarb Shift Tint
  "001864", // Gray Blue Tint
  "001867", // Olive Smoke Tint
  "001877", // Olivine Tint
  "001920", // Lemon Tint
  "001934", // Copper Tint
  "001948", // Purple Blue Tint
  "001964", // Lavender Gray Tint
  "001977", // Pine Green Tint
];

const buildStrikerMap = (): Record<string, StrikerType> => {
  const map: Record<string, StrikerType> = {};
  for (const code of BULLSEYE_GOLD_STRIKERS) map[code] = "GOLD";
  for (const code of BULLSEYE_CADMIUM_SELENIUM_STRIKERS) map[code] = "CADMIUM_SELENIUM";
  for (const code of BULLSEYE_TINT_SHIFT_STRIKERS) map[code] = "TINT_SHIFT";
  return map;
};

export const BULLSEYE_STRIKER_TYPES: Readonly<Record<string, StrikerType>> = buildStrikerMap();

// Bullseye spec is 663°C for ~2 hours, but real kilns vary — small efficient
// kilns often strike nearer 648–655°C. Accept a generous band so we don't
// scold users whose schedule works in practice.
export const GOLD_HOLD_TEMP_MIN_C = 645;
export const GOLD_HOLD_TEMP_MAX_C = 670;
export const GOLD_HOLD_MIN_MINUTES = 90;

// Bullseye "full fuse" range is 804–843°C (see temperatureRanges in
// unitHelpers.ts). Any segment reaching the minimum counts as a full fuse —
// higher-temperature processes (kiln casting, combing, etc.) pass through it.
export const BULLSEYE_FULL_FUSE_MIN_C = 804;
export const BULLSEYE_FULL_FUSE_MAX_C = 843;
